import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { Send, Mail, Clock, CheckCircle, ChevronDown, ChevronUp, Inbox } from 'lucide-react';

interface ConsultantMessage {
  id: string;
  consultant_id: string;
  subject: string;
  message: string;
  status: 'unread' | 'read' | 'replied';
  is_from_admin: boolean;
  read_by_consultant: boolean;
  created_at: string;
  consultant_profiles?: {
    full_name: string;
    email: string;
  };
}

interface ConsultantProfile {
  id: string;
  full_name: string;
  email: string;
}

export default function ConsultantMessages() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ConsultantMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ConsultantMessage | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<'inbox' | 'compose'>('inbox');
  const [consultants, setConsultants] = useState<ConsultantProfile[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  useEffect(() => {
    checkAuth();
    fetchMessages();
    fetchConsultants();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) navigate('/admin/login');
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('consultant_messages')
        .select(`*, consultant_profiles (full_name, email)`)
        .eq('is_from_admin', false)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultants = async () => {
    const { data } = await supabase
      .from('consultant_profiles')
      .select('id, full_name, email')
      .is('soft_deleted_at', null)
      .order('full_name');
    setConsultants(data || []);
  };

  const markAsRead = async (messageId: string) => {
    await supabase.from('consultant_messages').update({ status: 'read' }).eq('id', messageId);
    fetchMessages();
  };

  const handleReply = async () => {
    if (!selectedMessage || !replyText.trim()) return;
    setSending(true);
    try {
      await supabase.from('consultant_messages').update({ status: 'replied' }).eq('id', selectedMessage.id);
      setReplyText('');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSendToConsultant = async () => {
    if (!selectedConsultantId || !newSubject.trim() || !newMessage.trim()) return;
    setSending(true);
    try {
      const { error } = await supabase.from('consultant_messages').insert({
        consultant_id: selectedConsultantId,
        subject: newSubject.trim(),
        message: newMessage.trim(),
        is_from_admin: true,
        status: 'unread',
        read_by_consultant: false,
      });
      if (error) throw error;
      setSelectedConsultantId('');
      setNewSubject('');
      setNewMessage('');
      setSendSuccess('Message sent to consultant successfully.');
      setTimeout(() => setSendSuccess(''), 4000);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Workday Consultant Messages</h1>
              <p className="text-gray-600">
                View messages from consultants and send messages to them
                {unreadCount > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    {unreadCount} unread
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex gap-1 mb-6 border-b border-gray-200">
            <button
              onClick={() => { setActiveTab('inbox'); setSelectedMessage(null); }}
              className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                activeTab === 'inbox'
                  ? 'border-[#40E0D0] text-[#40E0D0]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2"><Inbox className="w-4 h-4" />Inbox from Consultants</span>
            </button>
            <button
              onClick={() => { setActiveTab('compose'); setSelectedMessage(null); }}
              className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                activeTab === 'compose'
                  ? 'border-[#40E0D0] text-[#40E0D0]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="flex items-center gap-2"><Send className="w-4 h-4" />Send to Consultant</span>
            </button>
          </div>

          {activeTab === 'inbox' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
                  </div>
                  {loading ? (
                    <div className="p-8 text-center text-gray-600">Loading messages...</div>
                  ) : messages.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">No messages yet.</div>
                  ) : (
                    <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          onClick={() => {
                            setSelectedMessage(message);
                            if (message.status === 'unread') markAsRead(message.id);
                          }}
                          className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedMessage?.id === message.id ? 'bg-[#40E0D0]/5' : ''
                          } ${message.status === 'unread' ? 'border-l-4 border-[#40E0D0]' : ''}`}
                        >
                          <div className="flex items-start justify-between mb-1">
                            <h3 className={`font-medium text-sm ${message.status === 'unread' ? 'text-gray-900' : 'text-gray-600'}`}>
                              {message.consultant_profiles?.full_name || 'Unknown Consultant'}
                            </h3>
                            {message.status === 'replied' && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                          </div>
                          <p className="text-sm text-gray-600 mb-1 truncate">{message.subject}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            {new Date(message.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2">
                {selectedMessage ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">{selectedMessage.subject}</h2>
                          <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                            <Mail className="h-4 w-4" />
                            {selectedMessage.consultant_profiles?.email}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          selectedMessage.status === 'unread' ? 'bg-blue-100 text-blue-800'
                          : selectedMessage.status === 'read' ? 'bg-gray-100 text-gray-800'
                          : 'bg-green-100 text-green-800'
                        }`}>
                          {selectedMessage.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Message</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Reply</h3>
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply here..."
                          rows={5}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                        />
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={handleReply}
                            disabled={sending || !replyText.trim()}
                            className="flex items-center gap-2 px-6 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                          >
                            <Send className="h-4 w-4" />
                            {sending ? 'Sending...' : 'Send Reply'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Select a message to view and reply</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'compose' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-5">Send Message to Consultant</h2>

                {sendSuccess && (
                  <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm font-medium">
                    {sendSuccess}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>
                    <select
                      value={selectedConsultantId}
                      onChange={e => setSelectedConsultantId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    >
                      <option value="">Select a consultant...</option>
                      {consultants.map(c => (
                        <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={newSubject}
                      onChange={e => setNewSubject(e.target.value)}
                      placeholder="Enter subject..."
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Write your message to the consultant..."
                      rows={7}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleSendToConsultant}
                      disabled={sending || !selectedConsultantId || !newSubject.trim() || !newMessage.trim()}
                      className="flex items-center gap-2 px-6 py-2.5 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                    >
                      <Send className="h-4 w-4" />
                      {sending ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
